import React, { useState } from "react";
import { Select, styled, Box, FormHelperText, Tooltip } from "@mui/material";
import type { SelectProps } from "@mui/material";
import { tokens } from "../../theme/tokens";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface TrackNowSelectProps extends Omit<SelectProps, 'variant'> {
  label?: string;
  helperText?: string;
  success?: boolean;
  successMessage?: string;
  showDisabledTooltip?: boolean;
}

const StyledSelect = styled(Select)<TrackNowSelectProps>(({ theme, success, error }) => ({
  height: '48px',
  borderRadius: tokens.radius.pill,
  fontFamily: 'var(--tn-font-family)',
  fontSize: '16px',
  fontWeight: 400,
  backgroundColor: tokens.colors.offWhite,

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: tokens.colors.grey,
    borderWidth: '1px',
    borderRadius: tokens.radius.pill,
  },

  '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1px',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: `${theme.palette.primary.main} !important`,
    borderWidth: '2px !important',
    borderRadius: `${tokens.radius.pill}px !important`,
  },

  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: tokens.colors.grey,
    borderWidth: '1px',
  },

  '&.Mui-disabled': {
    backgroundColor: tokens.colors.offWhite,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.colors.grey,
      borderWidth: '1px',
    },
    '& .MuiSelect-select': {
      color: tokens.colors.grey,
      cursor: 'not-allowed',
      WebkitTextFillColor: tokens.colors.grey,
    },
  },

  '& .MuiSelect-select': {
    padding: success || error ? '12px 68px 12px 14px !important' : '12px 44px 12px 14px !important',
    color: tokens.colors.offBlack,
    display: 'block',
    alignItems: 'center',
    minHeight: 'unset',
    boxSizing: 'border-box',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '& .MuiSelect-select.MuiSelect-multiple': {
    display: 'block',
    alignItems: 'center',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minHeight: 'unset',
  },

  '& .MuiSelect-icon': {
    color: theme.palette.primary.main,
    right: success || error ? '40px' : '12px',
  },

  '&.Mui-focused .MuiSelect-select': {
    color: theme.palette.primary.main,
  },

  ...(success && {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.colors.grey,
      borderWidth: '1px',
    },
  }),
}));

const IconContainer = styled(Box)(() => ({
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  zIndex: 1,
}));

const LabelContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '6px',
}));

const StyledLabel = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: tokens.colors.grey,
}));

const OptionalTag = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  color: '#9E9E9E',
}));

const StyledHelperText = styled(FormHelperText)<{ success?: boolean }>(({ success, error }) => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  textTransform: 'none',
  marginLeft: 0,
  marginTop: '4px',

  ...(error && {
    color: tokens.colors.error,
  }),

  ...(success && {
    color: tokens.colors.success,
  }),
}));

export const TrackNowSelect = ({
  label,
  helperText,
  success,
  successMessage,
  error,
  required,
  disabled,
  showDisabledTooltip = true,
  ...props
}: TrackNowSelectProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const displayHelperText = success ? successMessage : helperText;
  const mergedMenuProps = {
    ...props.MenuProps,
    PaperProps: {
      ...props.MenuProps?.PaperProps,
      sx: {
        borderRadius: '10px',
        mt: 1,
        '& .MuiMenuItem-root': {
          fontFamily: 'Area Normal, Inter, sans-serif',
          fontSize: '16px',
          fontWeight: 400,
        },
        ...(props.MenuProps?.PaperProps as any)?.sx,
      },
    },
  };

  const selectComponent = (
    <Box position="relative">
      <StyledSelect
        {...props}
        success={success}
        variant="outlined"
        required={false}
        disabled={disabled}
        error={error}
        IconComponent={KeyboardArrowDownIcon}
        onFocus={(e) => {
          setIsFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          props.onBlur?.(e);
        }}
        MenuProps={mergedMenuProps}
      />
      {success && (
        <IconContainer>
          <CheckCircleOutlineIcon sx={{ color: tokens.colors.success, fontSize: '18px' }} />
        </IconContainer>
      )}
      {error && (
        <IconContainer>
          <ReportProblemOutlinedIcon sx={{ color: tokens.colors.error, fontSize: '18px' }} />
        </IconContainer>
      )}
    </Box>
  );

  return (
    <Box>
      {label && (
        <LabelContainer>
          <StyledLabel>
            {label}
            {required && <span style={{ color: tokens.colors.error, marginLeft: '4px' }}>*</span>}
          </StyledLabel>
          {!required && isFocused && <OptionalTag>Optional</OptionalTag>}
        </LabelContainer>
      )}
      {disabled && showDisabledTooltip ? (
        <Tooltip title="Complete required fields to enable this field" arrow>
          <Box>{selectComponent}</Box>
        </Tooltip>
      ) : (
        selectComponent
      )}
      {displayHelperText && (
        <StyledHelperText success={success} error={error}>
          {displayHelperText}
        </StyledHelperText>
      )}
    </Box>
  );
};
